import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DollarSign, ShoppingBag, Users, Palette, RefreshCw, Sparkles, Upload, FileText, Mail, Trash2, Archive, ArchiveRestore, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, UserCog, Search, Shield, Receipt, FileDown, Edit3, Plus, Package, Send, Reply, X, Flag, ShieldCheck, ShieldAlert, ShieldX, Crown, Award, Gift, Minus, Power, PowerOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import RichEditor from '@/components/RichEditor';
import { useCurrency } from '@/context/CurrencyContext';
import { useConfirm } from '@/context/ConfirmContext';
import Pagination from '@/components/Pagination';
import BulkActionBar from '@/components/BulkActionBar';
import { useSelection } from '@/lib/useSelection';
import { openInvoice } from '@/lib/invoice';
import { Kpi } from './_shared';

export const MessagesSection = () => {
  const { refreshStats } = useOutletContext();
  const confirm = useConfirm();
  const [msgs, setMsgs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [viewing, setViewing] = useState(null);
  const [reply, setReply] = useState('');

  const load = () => api.get('/admin/contact').then(({ data }) => setMsgs(data));
  useEffect(() => { load(); }, []);

  const filtered = msgs.filter(m => filter === 'all' ? true : filter === 'unread' ? !m.read : m.read);

  const markRead = async (id) => { try { await api.patch(`/admin/contact/${id}/read`); load(); refreshStats(); } catch { /* swallow — not critical */ } };
  const del = async (id) => {
    const ok = await confirm({ title: 'Delete this message?', description: 'This cannot be undone.', confirmLabel: 'Delete', variant: 'danger' });
    if (!ok) return;
    try { await api.delete(`/admin/contact/${id}`); toast.success('Deleted'); load(); refreshStats(); }
    catch { toast.error('Failed'); }
  };
  const openMsg = (m) => { setViewing(m); setReply(m.reply || ''); if (!m.read) markRead(m.id); };
  const submitReply = async () => {
    if (!reply.trim()) { toast.error('Enter a reply'); return; }
    try {
      await api.patch(`/admin/contact/${viewing.id}`, { reply, status: 'replied' });
      toast.success('Reply saved');
      setViewing(null); setReply('');
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <Card className="jw-card overflow-hidden">
      <div className="p-4 border-b flex flex-wrap items-center gap-2" style={{ borderColor: 'var(--jw-border)' }}>
        <Mail className="w-4 h-4 text-orange-400" />
        <h3 className="font-semibold">Contact Submissions</h3>
        <span className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{msgs.length} total · {msgs.filter(m => !m.read).length} unread</span>
        <div className="ml-auto">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-32" data-testid="admin-messages-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="unread">Unread</SelectItem><SelectItem value="read">Read</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="p-10 text-center" style={{ color: 'var(--jw-text-muted)' }}>No messages.</div>
      ) : (
        <div className="divide-y" style={{ borderColor: 'var(--jw-border)' }}>
          {filtered.map(m => (
            <div key={m.id} className={`p-4 ${!m.read ? 'jw-panel' : ''}`} data-testid={`admin-contact-${m.id}-row`}>
              <div className="flex items-start gap-3">
                {!m.read && <div className="w-2 h-2 rounded-full jw-gradient-bg mt-2" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <p className="font-semibold">{m.name}</p>
                    <span className="text-xs" style={{ color: 'var(--jw-text-subtle)' }}>&lt;{m.email}&gt;</span>
                    <span className="text-xs" style={{ color: 'var(--jw-text-subtle)' }}>· {new Date(m.created_at).toLocaleString()}</span>
                    {m.status === 'replied' && <Badge variant="secondary" className="text-[10px]">REPLIED</Badge>}
                  </div>
                  <p className="font-medium mt-1">{m.subject}</p>
                  <p className="text-sm mt-1 whitespace-pre-wrap line-clamp-2" style={{ color: 'var(--jw-text-muted)' }}>{m.message}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" onClick={() => openMsg(m)} data-testid={`admin-contact-${m.id}-view-button`} className="jw-btn-gloss-ghost h-8"><Reply className="w-4 h-4 mr-1" /> View / Reply</Button>
                  <Button size="icon" variant="ghost" onClick={() => del(m.id)} data-testid={`admin-contact-${m.id}-delete-button`} className="text-rose-400"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent data-testid="admin-contact-view-dialog" className="max-w-lg">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle>{viewing.subject}</DialogTitle>
                <div className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{viewing.name} &lt;{viewing.email}&gt; · {new Date(viewing.created_at).toLocaleString()}</div>
              </DialogHeader>
              <div className="jw-panel p-3 max-h-48 overflow-auto text-sm whitespace-pre-wrap" style={{ color: 'var(--jw-text)' }}>{viewing.message}</div>
              <div>
                <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Your reply (internal note — stored on this message)</label>
                <Textarea rows={5} value={reply} onChange={(e) => setReply(e.target.value)} data-testid="admin-contact-reply-input" />
              </div>
              <DialogFooter>
                <a href={`mailto:${viewing.email}?subject=Re: ${encodeURIComponent(viewing.subject)}&body=${encodeURIComponent(reply)}`} className="text-sm underline" style={{ color: 'var(--jw-text-muted)' }} data-testid="admin-contact-mailto-link">Open in email client</a>
                <div className="flex-1" />
                <Button variant="ghost" onClick={() => setViewing(null)}>Close</Button>
                <Button onClick={submitReply} data-testid="admin-contact-reply-save-button" className="jw-btn-glossy"><Reply className="w-4 h-4 mr-1" /> Save reply</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MessagesSection;
